import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAllEstimates, saveEstimate, softDeleteEstimate, duplicateEstimate, updateEstimate } from "../../services/azureCostEstimatesService";
import { copyVariantsToEstimate } from "../../services/azureCostEstimateVariantsService";
import { getAllProjectsWithTags } from "../../services/azureProjectsService";
import { canEdit, isFreelancer } from "../../utils/permissions";
import { useFreelancerProjects } from "../../hooks/useFreelancerProjects";
import { useAuth } from "../../hooks/useAuth";
import { parseExcelFile } from "../../services/costs/excelImport";
import { EstimateTableProvider } from "../../services/costs/providers/estimateTableProvider";
import ExcelImportPreview from "./ExcelImportPreview";

const getInitials = (name) => {
  if (!name) return "?";
  // Handle email addresses
  const displayName = name.includes("@") ? name.split("@")[0] : name;
  const parts = displayName.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return displayName.substring(0, 2).toUpperCase();
};

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const EstimateList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canAccessProject, isFreelancerUser } = useFreelancerProjects(user);

  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    client: "",
    date: new Date().toISOString().split("T")[0],
    linkedProjectCode: "",
    selectedPhase: "",
  });
  const [formError, setFormError] = useState("");

  // Import state
  const [importData, setImportData] = useState(null); // { tree, meta, customColumns }
  const [importError, setImportError] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Rechtsklick-Kontextmenue + Inline-Umbenennen
  const [contextMenu, setContextMenu] = useState(null); // { x, y, estimate }
  const [editingEstimateId, setEditingEstimateId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  const defaultPhases = [
    "21 - Machbarkeit",
    "31 - Vorprojekt",
    "32 - Bauprojekt",
    "33 - Bewilligung",
    "41 - Ausschreibung",
  ];

  const projectFilter = searchParams.get("project");
  const canCreateDossier = canEdit(user, "kostenschaetzung");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [estimatesData, projectsData] = await Promise.all([
        getAllEstimates(),
        getAllProjectsWithTags("project"),
      ]);
      setEstimates(estimatesData);
      setProjects(projectsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (code) => {
    if (!code) return "";
    const project = projects.find((p) => p.code === code);
    return project ? `${code} - ${project.title}` : code;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredEstimates = estimates
    .filter((est) => {
      // Freelancer filter: only show estimates linked to assigned projects
      if (isFreelancerUser) {
        if (!est.linkedProjectCode) return false;
        if (!canAccessProject(est.linkedProjectCode)) return false;
      }
      // Project filter from URL
      if (projectFilter && est.linkedProjectCode !== projectFilter) return false;
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          est.name.toLowerCase().includes(term) ||
          est.linkedProjectCode.toLowerCase().includes(term)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      const valA = (a[sortField] || "").toLowerCase();
      const valB = (b[sortField] || "").toLowerCase();
      return valA.localeCompare(valB) * dir;
    });

  const handleSave = async () => {
    if (!formData.linkedProjectCode) {
      setFormError("Projekt ist erforderlich");
      return;
    }
    if (!formData.selectedPhase) {
      setFormError("Phase ist erforderlich");
      return;
    }

    try {
      setSaving(true);
      setFormError("");

      // Build name: use custom name, or fall back to phase name
      const dossierName = formData.name.trim() || formData.selectedPhase;

      const saved = await saveEstimate({
        ...formData,
        name: dossierName,
        phases: defaultPhases,
        currentPhase: formData.selectedPhase,
        createdBy: user?.userDetails || user?.email || user?.name || "",
      });
      setShowModal(false);
      setFormData({ name: "", client: "", date: new Date().toISOString().split("T")[0], linkedProjectCode: "", selectedPhase: "" });
      navigate(`/kosten/${saved.id}`);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = "";

    try {
      setImportError("");
      const result = await parseExcelFile(file);
      setImportData(result);
    } catch (err) {
      setImportError(err.message);
      console.error("Excel parse error:", err);
    }
  };

  const handleImport = async ({ tree, projectCode, phase, dossierName, meta, customColumnsToImport }) => {
    try {
      setImporting(true);
      setImportError("");

      // 1. Create the dossier
      const saved = await saveEstimate({
        name: dossierName,
        client: meta.author || "",
        date: meta.date || new Date().toISOString().split("T")[0],
        linkedProjectCode: projectCode,
        phases: defaultPhases,
        currentPhase: phase,
        createdBy: user?.userDetails || user?.email || user?.name || "",
      });

      // 2. Save BKP nodes via provider
      const provider = new EstimateTableProvider();
      const ops = Object.values(tree).map((node) => ({
        kind: "upsert",
        node: { ...node, phase },
      }));
      await provider.saveBatch(saved.id, ops);

      // 3. Save custom columns if any
      if (customColumnsToImport && customColumnsToImport.length > 0) {
        const colOps = [];
        const colDefs = [];

        for (let i = 0; i < customColumnsToImport.length; i++) {
          const col = customColumnsToImport[i];
          let colId;

          if (col.action === "add_to_tags") {
            // Merge values into the node's built-in tags array
            for (const [code, value] of Object.entries(col.values)) {
              if (tree[code]) {
                const newTags = value.split(",").map((t) => t.trim()).filter(Boolean);
                if (newTags.length === 0 && value.trim()) newTags.push(value.trim());
                tree[code].tags = [...new Set([...(tree[code].tags || []), ...newTags])];
              }
            }
            continue;
          }

          if (col.action === "import_new") {
            // crypto.randomUUID ist kollisionsfrei; Date.now+Math.random kann im ms-Fenster kollidieren.
            colId = `col_${crypto.randomUUID()}`;
            const colDef = {
              id: colId,
              name: col.headerName,
              type: "tags",
              order: i,
              hidden: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            colDefs.push(colDef);
            colOps.push({ kind: "upsert", column: colDef });
          } else if (col.action.startsWith("map_to:")) {
            colId = col.action.substring(7);
          } else {
            continue;
          }

          // Write values into tree nodes' customFields
          for (const [code, value] of Object.entries(col.values)) {
            if (tree[code]) {
              const tags = value.split(",").map((t) => t.trim()).filter(Boolean);
              if (tags.length === 0 && value.trim()) tags.push(value.trim());
              tree[code].customFields = tree[code].customFields || {};
              tree[code].customFields[colId] = tags;
            }
          }
        }

        // Re-save nodes that got custom fields
        const nodesWithFields = Object.values(tree).filter(
          (n) => n.customFields && Object.keys(n.customFields).length > 0
        );
        if (nodesWithFields.length > 0) {
          const fieldOps = nodesWithFields.map((node) => ({
            kind: "upsert",
            node: { ...node, phase },
          }));
          await provider.saveBatch(saved.id, fieldOps);
        }

        // Save column definitions
        if (colOps.length > 0) {
          await provider.saveCustomColumnsBatch(saved.id, colOps);
        }
      }

      setImportData(null);
      navigate(`/kosten/${saved.id}`);
    } catch (err) {
      console.error("Import error:", err);
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (e, estimate) => {
    e.stopPropagation();
    if (!window.confirm(`Dossier "${estimate.name}" wirklich löschen? Es wird 30 Tage aufbewahrt.`)) return;
    try {
      await softDeleteEstimate(estimate.id);
      setEstimates((prev) => prev.filter((est) => est.id !== estimate.id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleDuplicate = async (e, estimate) => {
    e.stopPropagation();
    // Abfrage: nur Metadaten oder komplette Kopie inkl. BKP-Items/Varianten/Custom Columns?
    const copyData = window.confirm(
      `Dossier "${estimate.name}" duplizieren.\n\n` +
      `OK: komplette Kopie inkl. aller BKP-Positionen, Varianten und eigenen Spalten.\n` +
      `Abbrechen: leeres Duplikat (nur Name, Projekt, Phasen).`
    );

    let saved = null;
    try {
      const createdBy = user?.userDetails || user?.email || user?.name || "";
      saved = await duplicateEstimate(estimate, createdBy);

      if (copyData) {
        // BKP-Items + Custom Columns via Provider, Varianten separat.
        // copyAllItems rollt eigene Teil-Kopien intern zurueck; copyVariantsToEstimate
        // wird sequenziell ausgefuehrt, damit ein BKP-Fehler nicht parallel bereits
        // Varianten anlegt, die wir hinterher wieder aufraeumen muessten.
        const provider = new EstimateTableProvider();
        await provider.copyAllItems(estimate.id, saved.id);
        await copyVariantsToEstimate(estimate.id, saved.id);
      }

      navigate(`/kosten/${saved.id}`);
    } catch (err) {
      console.error("Duplicate error:", err);
      // Cleanup: Ziel-Dossier wieder loeschen, damit kein leeres/halbkopiertes
      // Dossier in der Liste auftaucht. Bei Cleanup-Fehler nur loggen, weil der
      // Original-Fehler relevanter ist.
      if (saved?.id) {
        try {
          await softDeleteEstimate(saved.id);
        } catch (cleanupErr) {
          console.error("Cleanup nach Duplicate-Fehler fehlgeschlagen:", cleanupErr);
        }
      }
      window.alert(
        `Duplizieren fehlgeschlagen: ${err?.message || "Unbekannter Fehler"}\n\n` +
        `Das Ziel-Dossier wurde wieder entfernt. Bitte erneut versuchen.`
      );
    }
  };

  const handleRowClick = (estimate) => {
    if (editingEstimateId === estimate.id) return;
    navigate(`/kosten/${estimate.id}`);
  };

  // === Rechtsklick-Kontextmenue ===
  const handleContextMenu = (e, estimate) => {
    if (!canCreateDossier) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, estimate });
  };

  const startRename = (estimate) => {
    setContextMenu(null);
    setEditingEstimateId(estimate.id);
    setEditingName(estimate.name || "");
  };

  const cancelRename = () => {
    setEditingEstimateId(null);
    setEditingName("");
  };

  const saveRename = async () => {
    if (!editingEstimateId) return;
    const trimmed = editingName.trim();
    const current = estimates.find((e) => e.id === editingEstimateId);
    if (!current) {
      cancelRename();
      return;
    }
    if (!trimmed || trimmed === current.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await updateEstimate(editingEstimateId, { ...current, name: trimmed });
      setEstimates((prev) =>
        prev.map((est) => (est.id === editingEstimateId ? { ...est, name: trimmed } : est))
      );
      cancelRename();
    } catch (err) {
      console.error("Rename error:", err);
      // Name-Feld bleibt geoeffnet, damit der Nutzer erneut versuchen kann.
    } finally {
      setRenameSaving(false);
    }
  };

  // Kontextmenue schliessen bei Klick/Scroll/Escape ausserhalb.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Fokus auf Input bei Rename-Start.
  useEffect(() => {
    if (editingEstimateId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingEstimateId]);

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <span className="sort-indicator">{sortDirection === "asc" ? "\u2191" : "\u2193"}</span>;
  };

  if (loading) {
    return <div className="loading">Kostenschätzungen werden geladen...</div>;
  }

  return (
    <div className="estimate-list">
      <div className="team-controls">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Dossier suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          {projectFilter && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate("/kosten")}
            >
              Filter aufheben
            </button>
          )}
        </div>
        <div className="table-info">
          {filteredEstimates.length} {filteredEstimates.length === 1 ? "Dossier" : "Dossiers"}
          {projectFilter && ` (Projekt: ${projectFilter})`}
        </div>
        {canCreateDossier && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <button
              className="btn btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              title="Kostenschätzung aus Excel importieren"
            >
              Excel importieren
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              Neues Dossier
            </button>
          </>
        )}
      </div>

      <div className="team-table-container">
        <table className="team-table team-table-compact">
          <thead>
            <tr>
              <th>Projekt</th>
              <th
                className={`sortable ${sortField === "name" ? "active" : ""}`}
                onClick={() => handleSort("name")}
              >
                Name {renderSortIndicator("name")}
              </th>
              <th
                className={`sortable ${sortField === "updatedAt" ? "active" : ""}`}
                onClick={() => handleSort("updatedAt")}
              >
                Zuletzt bearbeitet {renderSortIndicator("updatedAt")}
              </th>
              <th>Phase</th>
              {canCreateDossier && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filteredEstimates.length === 0 ? (
              <tr>
                <td colSpan={canCreateDossier ? 5 : 4} style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>
                  {projectFilter
                    ? "Keine Kostenschätzungen für dieses Projekt vorhanden."
                    : "Keine Kostenschätzungen vorhanden."}
                </td>
              </tr>
            ) : (
              filteredEstimates.map((est) => {
                const now = Date.now();
                const recentEditors = (est.editors || [])
                  .filter((e) => now - new Date(e.lastActive).getTime() < THIRTY_DAYS)
                  .sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));

                const isEditing = editingEstimateId === est.id;
                return (
                  <tr
                    key={est.id}
                    className="team-row clickable-row"
                    onClick={() => handleRowClick(est)}
                    onContextMenu={(e) => handleContextMenu(e, est)}
                  >
                    <td>{est.linkedProjectCode || ""}</td>
                    <td className="cell-name" onClick={(e) => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          className="cell-name-input"
                          value={editingName}
                          disabled={renameSaving}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={saveRename}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveRename();
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                        />
                      ) : (
                        est.name
                      )}
                    </td>
                    <td className="cell-updated">
                      {est.updatedAt ? new Date(est.updatedAt).toLocaleDateString("de-CH") : ""}
                      {recentEditors.length > 0 && (
                        <span className="editor-avatars">
                          {recentEditors.slice(0, 3).map((editor) => (
                            <span
                              key={editor.name}
                              className="editor-avatar"
                              title={`${editor.name} (${new Date(editor.lastActive).toLocaleDateString("de-CH")})`}
                            >
                              {getInitials(editor.name)}
                            </span>
                          ))}
                          {recentEditors.length > 3 && (
                            <span className="editor-avatar editor-avatar-more" title={recentEditors.map((e) => e.name).join(", ")}>
                              +{recentEditors.length - 3}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      {est.currentPhase && (
                        <span className="phase-badge">{est.currentPhase}</span>
                      )}
                    </td>
                    {canCreateDossier && (
                      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn"
                          onClick={(e) => handleDuplicate(e, est)}
                          title="Dossier duplizieren"
                        >
                          &#x2398;
                        </button>
                        <button
                          className="action-btn action-btn-danger"
                          onClick={(e) => handleDelete(e, est)}
                          title="Dossier löschen"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {importError && (
        <div className="error-message" style={{ margin: "0.5rem 0" }}>
          Import-Fehler: {importError}
          <button className="btn btn-sm" onClick={() => setImportError("")} style={{ marginLeft: "0.5rem" }}>×</button>
        </div>
      )}

      {/* Import Preview Modal */}
      {importData && (
        <ExcelImportPreview
          tree={importData.tree}
          meta={importData.meta}
          customColumns={importData.customColumns}
          mismatches={importData.mismatches || {}}
          existingCustomColumns={[]}
          projects={projects}
          defaultPhases={defaultPhases}
          onImport={handleImport}
          onCancel={() => setImportData(null)}
        />
      )}

      {/* Rechtsklick-Kontextmenue fuer Dossiers */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header" title={contextMenu.estimate.name}>
            {contextMenu.estimate.name}
          </div>
          <button
            type="button"
            className="context-menu-item"
            onClick={() => startRename(contextMenu.estimate)}
          >
            Umbenennen
          </button>
        </div>
      )}

      {/* Modal: Neues Dossier */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content estimate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neues Kostenschätzungs-Dossier</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {formError && <div className="error-message">{formError}</div>}
              <div className="form-group">
                <label>Projekt *</label>
                <select
                  value={formData.linkedProjectCode}
                  onChange={(e) => setFormData({ ...formData, linkedProjectCode: e.target.value })}
                  autoFocus
                >
                  <option value="">-- Projekt wählen --</option>
                  {projects.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.code} - {p.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Phase *</label>
                <select
                  value={formData.selectedPhase}
                  onChange={(e) => setFormData({ ...formData, selectedPhase: e.target.value })}
                  disabled={!formData.linkedProjectCode}
                >
                  <option value="">-- Phase wählen --</option>
                  {defaultPhases.map((phase) => (
                    <option key={phase} value={phase}>
                      {phase}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Leer lassen für Phasenname"
                  disabled={!formData.selectedPhase}
                />
              </div>
              <div className="form-group">
                <label>Auftraggeber</label>
                <input
                  type="text"
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="Auftraggeber"
                  disabled={!formData.linkedProjectCode}
                />
              </div>
              <div className="form-group">
                <label>Datum</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  disabled={!formData.linkedProjectCode}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Speichern..." : "Dossier erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateList;
